import { isFunction, find } from 'underscore'
import eventCallbacks from '@/configuration/sources/EventCallbacks.yml'
import { Queue } from '@/common/model'
import { CallbackPreventDefault } from '@/common/errors/CallbackPreventDefault'
import { KR } from '@/host/KR'

export default class CallbackHandler {
  constructor({ $bus, $store, logger }) {
    this.events = eventCallbacks
    this.$store = $store
    this.$bus = $bus
    this.logger = logger
    this.task = new Queue()
    this.resetCallbacks()
  }

  resetCallbacks() {
    this.callbacks = this.events.reduce((acc, x) => {
      acc[x] = []
      return acc
    }, {})
  }

  removeEventCallbacks(eventName, callback) {
    return new Promise((resolve, reject) => {
      if (eventName) {
        if (this.events.indexOf(eventName) !== -1) {
          if (callback) {
            if (this.isCallbackAlreadyAdd(eventName, callback)) {
              this.removeCallback(eventName, callback)
              resolve()
            } else {
              reject(
                new Error(
                  `Event ${eventName}: the following callback was not found ${callback.toString()}`
                )
              )
            }
          } else {
            this.removeCallbackByEvent(eventName)
            resolve()
          }
        } else {
          reject(new Error(`${eventName} doesn't exist`))
        }
      } else {
        this.removeAll()
        resolve()
      }
    })
  }

  /**
   * Allow to execute a task prior to running callbacks.
   * This is was added to allow waiting for some specific condition beforehand.
   * E.g: waiting for GoogleSimulator complete closure before triggering
   * `onSubmit` callback.
   *
   * @param {function() => Promise<T> | T} task Job to execute before triggering events
   * @returns {Promise<T>}
   * @since KJS-6290
   */
  holdUntil(fn) {
    return this.task.push(fn)
  }

  async runCallbacks(eventName, args, formId = null) {
    const callbacks = this.callbacks[eventName]

    if (callbacks?.length) {
      if (eventName !== 'onLog') {
        this.logger.log('info', `[${eventName}] callback called`, {
          callback: callbacks
        })
      }

      const results = new Array(callbacks.length)
      let idx = 0

      // Call
      for (const callback of callbacks) {
        args = this.prepareArguments(args)
        results[idx] = await this.task.push(() => callback(args))
        ++idx
      }

      return results
    }

    return []
  }

  /**
   * Prepare argument sent to callback.
   * If false, return undefined
   * If it's not an object, return as such
   * Else return a deep copy.
   *
   * In the case of a deep copy being made an exception is made for KR object.
   * E.g: if payload == {
   *  cardInfo: {
   *    bin<string>,
   *    brand<object>
   *  },
   *  KR<KR>
   * }
   *
   * A deep copy of this object will be returned, but the KR object will remain
   * the same reference.
   *
   * This is for two reasons:
   * - JSON.stringify fails on circular references, which KR contains.
   * - We should return the same instance of KR anyway
   *
   * @param {Any} payload
   * @returns KJS-3406
   */
  prepareArguments(payload) {
    if (!payload) {
      return undefined
    }
    if (typeof payload !== 'object') {
      return payload
    }
    const original = {}
    const copy = JSON.parse(
      JSON.stringify(payload, (key, value) => {
        if (value instanceof KR || typeof value === 'function') {
          original[key] = value
          return undefined
        }
        return value
      })
    )
    for (const key in original) {
      copy[key] = original[key]
    }
    return copy
  }

  /**
   * Add a callback
   * pre
   */
  addCallback(eventName, callback) {
    return new Promise((resolve, reject) => {
      if (isFunction(callback)) {
        if (eventName !== 'onLog')
          this.logger.log('info', `[${eventName}] callback registered`, {
            callback
          })
        if (this.isCallbackAlreadyAdd(eventName, callback)) {
          this.removeCallback(eventName, callback)
          // Events with only one callback allowed
        } else if (
          eventName === 'onError' ||
          eventName === 'onOrderUpdate' ||
          eventName === 'onTransactionCreated' ||
          eventName === 'onSubmit'
        ) {
          this.removeCallbackByEvent(eventName)
        }
        this.callbacks[eventName].push(callback)
        resolve()
      } else {
        this.$store.dispatch('error', {
          errorCode: 'CLIENT_002',
          metadata: { eventName }
        })
        const { translate, onErrorTranslationLoaded } = this.$store.getters
        onErrorTranslationLoaded(() => {
          const msg = translate('CLIENT_002').replace('%function%', eventName)
          reject(new Error(msg))
        })
      }
    })
  }

  /**
   * @private
   */
  isCallbackAlreadyAdd(eventName, callback) {
    return (
      find(
        this.callbacks[eventName],
        x => x.toString() === callback.toString()
      ) !== undefined
    )
  }

  /**
   * Remove all callback
   * @private
   */
  removeAll() {
    this.resetCallbacks()
  }

  /**
   * Remove all callback of an event
   * @private
   */
  removeCallbackByEvent(eventName) {
    const hadCallbacks = this.callbacks[eventName].length
    this.callbacks[eventName] = []
    if (hadCallbacks) {
      console.warn(`Existing ${eventName} callbacks have been removed`)
      if (eventName !== 'onLog')
        this.logger.log('info', `[${eventName}] callbacks removed`)
    }
  }

  /**
   * Remove a precise callback
   * @private
   */
  removeCallback(eventName, func) {
    const temp = this.callbacks[eventName].filter(
      x => x.toString() !== func.toString()
    )
    this.callbacks[eventName] = temp
    console.warn(`Existing ${eventName} callback's been removed`)
    if (eventName !== 'onLog')
      this.logger.log('info', `[${eventName}] callback removed`, {
        callback: func
      })
  }

  onSubmitEventListener() {
    // Adds a new interceptor to the stack
    this.$store.dispatch('addInterceptor', {
      name: 'onSubmit',
      promise: (paymentData, formId) => {
        return new Promise(async (resolve, reject) => {
          // If the callback result is not true (false any of the items), kill the execution
          const results = await this.runCallbacks(
            'onSubmit',
            paymentData,
            formId
          )
          if (!~results.indexOf(false)) resolve()
          else {
            this.$store.dispatch('update', { redirectTransaction: null })
            reject(
              new CallbackPreventDefault(
                'onSubmit callback explicitely returned false'
              )
            )
          }
        })
      }
    })
  }

  onOrderUpdateEventListener() {
    // Adds a new interceptor to the stack
    this.$store.dispatch('addInterceptor', {
      name: 'onOrderUpdate',
      promise: (paymentData, formId) => {
        return new Promise(async (resolve, reject) => {
          // If the callback result is not true (false any of the items), kill the execution
          const results = await this.runCallbacks(
            'onOrderUpdate',
            paymentData,
            formId
          )
          if (!~results.indexOf(false)) resolve()
          else {
            this.$store.dispatch('update', { redirectTransaction: null })
            reject(
              new CallbackPreventDefault(
                'onOrderUpdate callback explicitely returned false'
              )
            )
          }
        })
      }
    })
  }

  onTransactionCreatedEventListener() {
    // Adds a new interceptor to the stack
    this.$store.dispatch('addInterceptor', {
      name: 'onTransactionCreated',
      promise: (paymentData, formId) => {
        return new Promise(async (resolve, reject) => {
          // If the callback result is not true (false any of the items), kill the execution
          const results = await this.runCallbacks(
            'onTransactionCreated',
            paymentData,
            formId
          )
          if (!~results.indexOf(false)) resolve()
          else {
            this.$store.dispatch('update', { redirectTransaction: null })
            reject(
              new CallbackPreventDefault(
                'onTransactionCreated callback explicitely returned false'
              )
            )
          }
        })
      }
    })
  }

  onClickEventListener() {
    // Adds a new interceptor to the stack
    this.$store.dispatch('addInterceptor', {
      name: 'paymentButton',
      promise: formId => {
        return new Promise(async (resolve, reject) => {
          // If the callback result is not true (false any of the items), kill the execution
          const results = await this.runCallbacks(
            'button.onClick',
            null,
            formId
          )
          if (!~results.indexOf(false)) resolve()
          else {
            reject(
              new CallbackPreventDefault(
                'button.onClick callback explicitely returned false'
              )
            )
          }
        })
      }
    })
  }

  onSmartFormClickEventListener() {
    // Adds a new interceptor to the stack
    this.$store.dispatch('addInterceptor', {
      name: 'onSmartFormClick',
      promise: methodInfo => {
        return new Promise(async (resolve, reject) => {
          // If the callback result is not true (false any of the items), kill the execution
          const results = await this.runCallbacks(
            'smartForm.onClick',
            methodInfo
          )
          if (!~results.indexOf(false)) resolve()
          else {
            reject(
              new CallbackPreventDefault(
                'smartForm.onClick callback explicitely returned false'
              )
            )
          }
        })
      }
    })
  }

  onPaymentTokenDeletedEventListener() {
    // Adds a new interceptor to the stack
    this.$store.dispatch('addInterceptor', {
      name: 'onPaymentTokenDeleted',
      promise: tokenData => {
        return new Promise(async (resolve, reject) => {
          // If the callback result is not true (false any of the items), kill the execution
          const results = await this.runCallbacks(
            'wallet.onPaymentTokenDeleted',
            tokenData
          )
          if (!~results.indexOf(false)) resolve()
          else {
            reject(
              new CallbackPreventDefault(
                'wallet.onPaymentTokenDeleted callback explicitely returned false'
              )
            )
          }
        })
      }
    })
  }
}
